/**
 * The shared AppSync GraphQL client.
 *
 * Every `lib/*` API module must take its client from here rather than calling
 * `generateClient()` itself, because *where Amplify gets configured relative to
 * the first GraphQL call* is the whole point of this module.
 *
 * The bug it fixes: `AuthContext` configures Amplify by dynamically importing
 * the SDK (`ensureAmplifyConfigured`), which is asynchronous. A page that
 * fetches on mount — ref mode is the worst case, firing match, tournament and
 * ref-session requests at once — races that import and usually wins, because
 * its own module was already loaded. Amplify resolves the API config *per
 * operation* (`resolveConfig` in @aws-amplify/api-graphql reads
 * `amplify.getConfig()` at call time), so the calls did not queue behind
 * configuration; they ran against an empty config and failed:
 *
 *   [WARN] GraphQLAPI resolveConfig - The API configuration is missing.
 *          This is likely due to Amplify.configure() not being called prior
 *          to generateClient().
 *   Failed to fetch match / Failed to fetch tournament / Failed to claim ref
 *   session / Failed to fetch clubs
 *   Match subscription error: Subscribe only available for AWS AppSync endpoint
 *
 * (the last one is the same cause — with no config there is no endpoint, so the
 * subscription has nothing to connect to.) All of it landed on one beta tester
 * inside a single second, mid-tournament.
 *
 * The fix is ordering, not awaiting. Configuration happens synchronously at
 * module evaluation below, before `generateClient()` and therefore before any
 * importer can hold a client. Awaiting would have meant touching hundreds of
 * call sites and could not work for subscriptions, which must return an
 * Observable synchronously.
 *
 * This module intentionally imports the SDK statically. It is only reachable
 * through the `lib/*` API modules, which are themselves only ever dynamically
 * imported (see the note in hooks/useQueries.ts), so the logged-out landing
 * page still never loads Amplify.
 */

import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/api';
import { configureAmplifyWith } from './amplify';

// Order matters: configure first, create the client second. Do not move this
// below the generateClient() call, and do not make it async.
configureAmplifyWith(Amplify);

export const client = generateClient();
